import './action-modals.scss';
import { FwVersionInfo } from '../../../../../dtos/version.dto';
import { observer } from 'mobx-react-lite';
import { useStores } from '../../../../store';
import usePromiseFeedback from '../../../../shared/promise-feedback/usePromiseFeedback';
import React, { useEffect, useMemo, useState } from 'react';
import CustomIcon, { IconType } from '../../../../shared/custom-icon/custom-icon';
import GenericModal from '../../../../shared/generic-modal/generic-modal';
import { CopyAction, DisableReasons, MainPages } from '../../../../../utils/enums';
import Alert from '../../../../shared/alert/alert';
import { PolicyActionDto, PolicyActionModal } from '../../../version-button/policy-action-modal';
import { usePull } from '../../../../../utils/pullPolicyUtil';
import { MAINTENANCE_ALERT } from '../../../../../utils/constants';
import { EventType } from '../../../../../dtos/analytics.dto';

interface PullPolicyModalProps {
    open: boolean;
    close: () => void;
    closePanel: () => void;
    sourceCircuitId?: string;
    versionData?: FwVersionInfo;
}

export const PullPolicyModal: React.FC<PullPolicyModalProps> = observer(({
    open,
    close,
    sourceCircuitId,
    versionData,
    closePanel
}) => {
    const { fwVersionStore, customerStore, uiStore } = useStores();
    const [promiseFeedbackFactory, promiseContextHolder] = usePromiseFeedback();
    const [isApplyPolicyVisible, setIsApplyPolicyVisible] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(true);
    const [actionInProgress, setActionInProgress] = useState<CopyAction>();
    const [showDraftWarning, setShowDraftWarning] = useState(!!fwVersionStore.draftVersion);
    const { pullAsDraft, pullAndApply } = usePull();

    useEffect(() => {
        if (open) {
            setIsApplyPolicyVisible(false);
            setIsModalVisible(true);
            setActionInProgress(undefined);
            setShowDraftWarning(!!fwVersionStore.draftVersion);
        }
    }, [open]);

    const closeModal = () => {
        setIsModalVisible(false);
        setIsApplyPolicyVisible(false);
        setActionInProgress(undefined);
        close();
    };

    const removeDraftIfNeeded = (versionId: string, versionSid: number, cb: () => void) => {
        const { draftVersion } = fwVersionStore;
        if (draftVersion) {
            promiseFeedbackFactory({
                promise: fwVersionStore.deleteVersion(draftVersion.externalId),
                successCallback: () => {
                    cb();
                },
                notifObj: { show: false }
            });
        } else {
            cb();
        }
    };
    const errorCb = () => {
        setActionInProgress(undefined);
    };
    const onVersionPullAsDraft = (versionId: string, versionSid: number) => {
        setActionInProgress(CopyAction.CopyAsDraft);
        const successCb = () => {
            closeModal();
            closePanel();
        };

        removeDraftIfNeeded(versionId, versionSid, () => {
            pullAsDraft(successCb, errorCb, versionData?.sid, sourceCircuitId);
        });
    };

    const onVersionPull = () => {
        setActionInProgress(CopyAction.CopyAndApply);
        setIsApplyPolicyVisible(true);
        setIsModalVisible(false);
    };

    const applyPolicy = (data: PolicyActionDto) => {
        const { policyName, policyComments } = data;
        fwVersionStore.sendGeneralEvent(EventType.LinkClicked, {
            button: 'pullAndApply',
            location: uiStore.mainPage === MainPages.POLICY_SELECTION ? 'policySelection' : 'policyManagerPullPolicyModal'
        });
        removeDraftIfNeeded(fwVersionStore.selectedFwVersion?.externalId || '', customerStore.serviceId, () => {
            const successCb = () => {
                closePanel();
                setIsApplyPolicyVisible(false);
                setActionInProgress(undefined);
            };
            pullAndApply(policyName ?? '', policyComments ?? '', successCb, errorCb, true, versionData?.sid, sourceCircuitId);
        });
    };

    const pullPolicyModalBody = useMemo(() => {
        let versionInfo = `V${versionData?.versionNumber}`;
        if (versionData?.versionName) {
            versionInfo += ` - ${versionData.versionName}`;
        }
        
        return <>
            <div className={'policy-action-modal-description'}>Origin</div>
            <div className={'policy-action-modal-details'}>
                <div><CustomIcon type={IconType.IP_CONTROL} />{sourceCircuitId}</div>
                <div>
                    <CustomIcon type={IconType.EMPTY_SHIELD} />{versionInfo}
                </div>
            </div>
            <div className={'policy-action-modal-description'}>Copy to</div>
            <div className={'policy-action-modal-details'}>
                <div><CustomIcon type={IconType.IP_CONTROL} />{customerStore.selectedLocation?.serviceName || customerStore.selectedLocation?.adiCircuitId}</div>
            </div>
            {showDraftWarning && <Alert message={'Copy policy will override the current draft policy.'} isLight={true}></Alert>}
        </>;
    }, [showDraftWarning, customerStore.selectedLocation, versionData]);

    const actionButtons = [
        {
            text: CopyAction.CopyAsDraft,
            onClick: () => {
                fwVersionStore.sendGeneralEvent(EventType.LinkClicked, {
                    button: 'pullAsDraft',
                    location: uiStore.mainPage === MainPages.POLICY_SELECTION ? 'policySelection' : 'policyManagerPullPolicyModal'
                });
                onVersionPullAsDraft(versionData?.externalId || '', customerStore.serviceId);
            },
            className: `secondary-btn ${actionInProgress === CopyAction.CopyAsDraft ? 'loading' : ''}`,
            disabled: fwVersionStore.isVersionDeactivate,
            disableReason: fwVersionStore.isVersionDeactivate ? DisableReasons.CREATE_DRAFT_NOT_AVAILABLE_DEACTIVATED : ''
        },
        {
            text: CopyAction.CopyAndApply,
            onClick: () => {
                onVersionPull();
            },
            className: `primary-btn ${actionInProgress === CopyAction.CopyAndApply ? 'loading' : ''}`,
            disabled: !customerStore.isAvailable,
            disableReason: !customerStore.isAvailable ? MAINTENANCE_ALERT : ''
        }
    ];

    return open ? <>
        <PolicyActionModal visible={isApplyPolicyVisible} close={closeModal} ok={applyPolicy} />
        <GenericModal open={isModalVisible}
            body={pullPolicyModalBody}
            onCancel={closeModal}
            title={'Copy Policy'}
            actionButtons={actionButtons}
            wrapClassName={'pull-policy-modal'} />
        {promiseContextHolder}
    </> : null;
});